import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft, FileText, Clock, AlertTriangle, CalendarClock,
  ListChecks, Flag, Mail, ShieldCheck, Database, Users, Building2,
  Handshake, UserCheck,
} from 'lucide-react';
import HussarTopNav from '../components/HussarTopNav';

/**
 * ReviewAktImigracyjnych — v4.0 Phase 6.
 *
 * Dedicated public demo route that renders the A–H immigration-record
 * review shape promised by the homepage "Zobacz demo weryfikacji" CTA.
 * Purely static / fictional; no backend calls, no PII, no forms.
 *
 * Route mounts at:
 *   /review-akt-imigracyjnych
 *   /immigration-record-review  (EN alias, same PL/EN i18n)
 */
const PAGE_BG = 'var(--hussar-bg-page, #0B1220)';
const TEXT_PRIMARY = 'var(--hussar-text-primary, #E5E7EB)';
const CYAN = '#7DD3FC';
const CYAN_STRONG = '#38BDF8';
const RED_SOFT = 'rgba(248,113,113,0.30)';

const setMeta = (selector, attr, value) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement(selector.startsWith('meta') ? 'meta' : 'link');
    const name = selector.match(/name="([^"]+)"/)?.[1];
    const prop = selector.match(/property="([^"]+)"/)?.[1];
    const rel = selector.match(/rel="([^"]+)"/)?.[1];
    if (name) el.setAttribute('name', name);
    if (prop) el.setAttribute('property', prop);
    if (rel) el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
};

const cardStyle = {
  background: 'rgba(15,23,42,0.55)',
  border: '1px solid rgba(148,163,184,0.15)',
  borderRadius: 14,
  padding: '26px 26px 22px',
  marginBottom: 20,
};

const sectionTitleStyle = {
  fontSize: 20,
  fontWeight: 700,
  color: '#F1F5F9',
  margin: '0 0 10px',
  letterSpacing: -0.2,
  display: 'flex',
  alignItems: 'center',
  gap: 10,
};

const labelBadgeStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 30,
  height: 30,
  borderRadius: 8,
  background: 'rgba(56,189,248,0.14)',
  border: '1px solid rgba(56,189,248,0.42)',
  color: CYAN_STRONG,
  fontSize: 13,
  fontWeight: 800,
  flex: '0 0 auto',
};

const bodyStyle = {
  fontSize: 15,
  lineHeight: 1.65,
  color: 'rgba(226,232,240,0.86)',
  margin: '0 0 12px',
};

const listStyle = {
  listStyle: 'none',
  padding: 0,
  margin: 0,
  display: 'grid',
  gap: 8,
};

const listItemStyle = {
  fontSize: 14,
  lineHeight: 1.55,
  color: 'rgba(226,232,240,0.88)',
  display: 'flex',
  alignItems: 'flex-start',
  gap: 10,
};

const bulletDotStyle = {
  flex: '0 0 auto',
  width: 6,
  height: 6,
  borderRadius: '50%',
  background: CYAN,
  marginTop: 8,
};

const SectionCard = ({ letter, title, icon: Icon, testidPrefix, children }) => (
  <section data-testid={`${testidPrefix}-section`} style={cardStyle}>
    <h2 data-testid={`${testidPrefix}-title`} style={sectionTitleStyle}>
      <span data-testid={`${testidPrefix}-label`} style={labelBadgeStyle}>{letter}</span>
      {Icon && <Icon className="w-5 h-5" aria-hidden="true" style={{ color: CYAN_STRONG }} />}
      <span>{title}</span>
    </h2>
    {children}
  </section>
);

const HumanReviewChip = ({ label }) => (
  <span
    data-testid="review-demo-human-review-chip"
    style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '4px 10px', borderRadius: 999,
      background: 'rgba(56,189,248,0.10)',
      border: `1px solid ${CYAN_STRONG}`,
      color: CYAN_STRONG,
      fontSize: 11, fontWeight: 700, letterSpacing: 0.4,
    }}
  >
    <UserCheck className="w-3 h-3" aria-hidden="true" />
    {label}
  </span>
);

const ReviewAktImigracyjnych = () => {
  const { t } = useTranslation();

  useEffect(() => {
    document.title = t('reviewDemo.metaTitle');
    setMeta('meta[name="description"]', 'content', t('reviewDemo.metaDescription'));
    setMeta('meta[property="og:title"]', 'content', t('reviewDemo.metaTitle'));
    setMeta('meta[property="og:description"]', 'content', t('reviewDemo.metaDescription'));
    setMeta('link[rel="canonical"]', 'href', 'https://hussarai.org/review-akt-imigracyjnych');

    // v4.0 Phase 8 · hreflang alternates. EN alias canonicalizes to PL origin.
    const setLink = (rel, hreflang, href) => {
      const sel = `link[rel="${rel}"][hreflang="${hreflang}"]`;
      let el = document.head.querySelector(sel);
      if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', rel);
        el.setAttribute('hreflang', hreflang);
        document.head.appendChild(el);
      }
      el.setAttribute('href', href);
    };
    setLink('alternate', 'pl-PL', 'https://hussarai.org/review-akt-imigracyjnych');
    setLink('alternate', 'en-GB', 'https://hussarai.org/immigration-record-review');
    setLink('alternate', 'x-default', 'https://hussarai.org/review-akt-imigracyjnych');

    return () => {
      const c = document.head.querySelector('link[rel="canonical"]');
      if (c) c.setAttribute('href', 'https://hussarai.org/');
    };
  }, [t]);

  // v4.0 Phase 8 · WebPage JSON-LD (safe-by-design local constant).
  const jsonLdWebPage = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': 'https://hussarai.org/review-akt-imigracyjnych#page',
    url: 'https://hussarai.org/review-akt-imigracyjnych',
    name: 'Demo weryfikacji akt imigracyjnych',
    description:
      'Zobacz strukturę weryfikacji A–H: podsumowanie, chronologia, braki, terminy, sygnały do sprawdzenia. Wynik wymaga weryfikacji przez człowieka.',
    inLanguage: 'pl-PL',
    isPartOf: { '@id': 'https://hussarai.org/#website' },
    about: { '@id': 'https://hussarai.org/#org' },
    significantLink: [
      'https://hussarai.org/bezpieczenstwo-i-zgodnosc',
      'https://hussarai.org/moje-akta-imigracyjne',
      'https://hussarai.org/dla-instytucji',
      'https://hussarai.org/dla-profesjonalistow',
    ],
  };

  const bItems = t('reviewDemo.sectionB.items', { returnObjects: true }) || [];
  const cItems = t('reviewDemo.sectionC.items', { returnObjects: true }) || [];
  const dItems = t('reviewDemo.sectionD.items', { returnObjects: true }) || [];
  const eItems = t('reviewDemo.sectionE.items', { returnObjects: true }) || [];
  const fItems = t('reviewDemo.sectionF.items', { returnObjects: true }) || [];

  return (
    <main
      data-testid="review-demo-page"
      style={{ minHeight: '100vh', background: PAGE_BG, color: TEXT_PRIMARY, padding: '48px 20px 96px' }}
    >
      <HussarTopNav />
      {/* v4.0 Phase 8 · WebPage JSON-LD (safe-by-design local constant). */}
      <script
        type="application/ld+json"
        data-testid="review-demo-jsonld-webpage"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLdWebPage) }}
      />
      <div style={{ maxWidth: 860, margin: '0 auto' }}>
        <Link
          to="/"
          data-testid="review-demo-back-link"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'rgba(125,211,252,0.85)', textDecoration: 'none', marginBottom: 26 }}
        >
          <ArrowLeft className="w-4 h-4" /> {t('reviewDemo.backLabel').replace('← ', '')}
        </Link>

        {/* Hero */}
        <p data-testid="review-demo-eyebrow" style={{ fontSize: 11, letterSpacing: 1.6, fontWeight: 700, color: CYAN, textTransform: 'uppercase', marginBottom: 14 }}>
          {t('reviewDemo.eyebrow')}
        </p>
        <h1 data-testid="review-demo-h1" style={{ fontSize: 'clamp(28px, 4vw, 42px)', fontWeight: 800, lineHeight: 1.2, marginBottom: 16, color: '#F1F5F9' }}>
          {t('reviewDemo.h1')}
        </h1>
        <p data-testid="review-demo-subtitle" style={{ fontSize: 17, lineHeight: 1.65, color: 'rgba(226,232,240,0.88)', marginBottom: 18 }}>
          {t('reviewDemo.subtitle')}
        </p>

        {/* Doctrine strip */}
        <section
          data-testid="review-demo-doctrine-strip"
          style={{ ...cardStyle, background: 'rgba(30,41,59,0.55)', borderLeft: `3px solid ${CYAN_STRONG}`, marginBottom: 30 }}
        >
          <p data-testid="review-demo-doctrine-line-1" style={{ ...bodyStyle, color: 'rgba(226,232,240,0.92)', marginBottom: 8 }}>
            {t('reviewDemo.doctrine.line1')}
          </p>
          <p data-testid="review-demo-doctrine-line-2" style={{ ...bodyStyle, color: 'rgba(226,232,240,0.88)', fontStyle: 'italic', marginBottom: 8 }}>
            {t('reviewDemo.doctrine.line2')}
          </p>
          <p data-testid="review-demo-doctrine-line-3" style={{ ...bodyStyle, color: CYAN_STRONG, fontWeight: 700, marginBottom: 0 }}>
            {t('reviewDemo.doctrine.line3')}
          </p>
        </section>

        <div data-testid="review-demo-human-review-badge-row" style={{ marginBottom: 22 }}>
          <HumanReviewChip label={t('reviewDemo.humanReviewBadge')} />
        </div>

        {/* Section A — Summary */}
        <SectionCard letter={t('reviewDemo.sectionA.label')} title={t('reviewDemo.sectionA.title')} icon={FileText} testidPrefix="review-demo-a">
          <p data-testid="review-demo-a-body" style={bodyStyle}>{t('reviewDemo.sectionA.body')}</p>
          <p data-testid="review-demo-a-review-note" style={{ ...bodyStyle, fontStyle: 'italic', color: CYAN_STRONG, marginBottom: 0 }}>
            {t('reviewDemo.sectionA.reviewNote')}
          </p>
        </SectionCard>

        {/* Section B — Chronology */}
        <SectionCard letter={t('reviewDemo.sectionB.label')} title={t('reviewDemo.sectionB.title')} icon={Clock} testidPrefix="review-demo-b">
          <ul data-testid="review-demo-b-list" style={listStyle}>
            {bItems.map((item) => (
              <li key={item} style={listItemStyle}>
                <span style={bulletDotStyle} />
                <span>{item}</span>
              </li>
            ))}
          </ul>
          <p data-testid="review-demo-b-consistency-note" style={{ ...bodyStyle, marginTop: 14, fontStyle: 'italic', color: 'rgba(226,232,240,0.78)' }}>
            {t('reviewDemo.sectionB.consistencyNote')}
          </p>
        </SectionCard>

        {/* Section C — Missing documents */}
        <SectionCard letter={t('reviewDemo.sectionC.label')} title={t('reviewDemo.sectionC.title')} icon={AlertTriangle} testidPrefix="review-demo-c">
          <p data-testid="review-demo-c-intro" style={bodyStyle}>{t('reviewDemo.sectionC.intro')}</p>
          <ul data-testid="review-demo-c-list" style={listStyle}>
            {cItems.map((item) => (
              <li key={item} style={listItemStyle}>
                <span style={bulletDotStyle} />
                <span>{item}</span>
              </li>
            ))}
          </ul>
          <p data-testid="review-demo-c-framing" style={{ ...bodyStyle, marginTop: 14, fontStyle: 'italic', color: 'rgba(226,232,240,0.78)' }}>
            {t('reviewDemo.sectionC.framing')}
          </p>
        </SectionCard>

        {/* Section D — Deadlines */}
        <SectionCard letter={t('reviewDemo.sectionD.label')} title={t('reviewDemo.sectionD.title')} icon={CalendarClock} testidPrefix="review-demo-d">
          <ul data-testid="review-demo-d-list" style={listStyle}>
            {dItems.map((item) => (
              <li key={item} style={listItemStyle}>
                <span style={bulletDotStyle} />
                <span>{item}</span>
              </li>
            ))}
          </ul>
          <p data-testid="review-demo-d-framing" style={{ ...bodyStyle, marginTop: 14, fontStyle: 'italic', color: 'rgba(226,232,240,0.78)' }}>
            {t('reviewDemo.sectionD.framing')}
          </p>
        </SectionCard>

        {/* Section E — Human checklist */}
        <SectionCard letter={t('reviewDemo.sectionE.label')} title={t('reviewDemo.sectionE.title')} icon={ListChecks} testidPrefix="review-demo-e">
          <p data-testid="review-demo-e-intro" style={bodyStyle}>{t('reviewDemo.sectionE.intro')}</p>
          <ul data-testid="review-demo-e-list" style={listStyle}>
            {eItems.map((item) => (
              <li key={item} style={listItemStyle}>
                <span style={bulletDotStyle} />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </SectionCard>

        {/* Section F — Review signals */}
        <SectionCard letter={t('reviewDemo.sectionF.label')} title={t('reviewDemo.sectionF.title')} icon={Flag} testidPrefix="review-demo-f">
          <p data-testid="review-demo-f-framing" style={{ ...bodyStyle, color: 'rgba(226,232,240,0.92)', fontWeight: 600 }}>
            {t('reviewDemo.sectionF.framing')}
          </p>
          <ul data-testid="review-demo-f-list" style={listStyle}>
            {fItems.map((item) => (
              <li key={item} style={listItemStyle}>
                <span style={bulletDotStyle} />
                <span>{item}</span>
              </li>
            ))}
          </ul>
          <p data-testid="review-demo-f-integrity" style={{ ...bodyStyle, marginTop: 14, fontStyle: 'italic', color: CYAN_STRONG }}>
            {t('reviewDemo.sectionF.integrity')}
          </p>
        </SectionCard>

        {/* Section G — Draft message */}
        <SectionCard letter={t('reviewDemo.sectionG.label')} title={t('reviewDemo.sectionG.title')} icon={Mail} testidPrefix="review-demo-g">
          <p data-testid="review-demo-g-intro" style={bodyStyle}>{t('reviewDemo.sectionG.intro')}</p>
          <pre
            data-testid="review-demo-g-body"
            style={{
              whiteSpace: 'pre-wrap', wordBreak: 'break-word',
              padding: 16, borderRadius: 10,
              background: 'rgba(2,6,23,0.55)',
              border: '1px solid rgba(148,163,184,0.18)',
              color: 'rgba(226,232,240,0.9)',
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
              fontSize: 13.5, lineHeight: 1.6,
              margin: 0,
            }}
          >
            {t('reviewDemo.sectionG.body')}
          </pre>
          <p data-testid="review-demo-g-note" style={{ ...bodyStyle, marginTop: 12, fontStyle: 'italic', color: 'rgba(226,232,240,0.78)' }}>
            {t('reviewDemo.sectionG.note')}
          </p>
        </SectionCard>

        {/* Section H — Disclaimer */}
        <SectionCard letter={t('reviewDemo.sectionH.label')} title={t('reviewDemo.sectionH.title')} icon={ShieldCheck} testidPrefix="review-demo-h">
          <p data-testid="review-demo-h-body" style={bodyStyle}>{t('reviewDemo.sectionH.body')}</p>
          <p data-testid="review-demo-h-human-review" style={{ ...bodyStyle, fontWeight: 600, color: CYAN_STRONG, marginBottom: 0 }}>
            {t('reviewDemo.sectionH.humanReview')}
          </p>
        </SectionCard>

        {/* v4.0 Phase 8 · Doctrine anchor — demo → trust page. */}
        <div data-testid="review-demo-doctrine-anchor-link" style={{ margin: '4px 0 20px' }}>
          <Link
            to="/bezpieczenstwo-i-zgodnosc"
            data-testid="review-demo-anchor-to-security"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: CYAN, fontWeight: 600, textDecoration: 'underline', fontSize: 15 }}
          >
            Osiem zasad bezpieczeństwa i doktryna danych
          </Link>
        </div>

        {/* Data governance section */}
        <section
          data-testid="review-demo-data-governance"
          style={{
            ...cardStyle,
            background: 'rgba(15,23,42,0.55)',
            borderLeft: `3px solid ${CYAN}`,
            marginTop: 12,
          }}
        >
          <h2 data-testid="review-demo-data-governance-title" style={{ ...sectionTitleStyle, gap: 8 }}>
            <Database className="w-5 h-5" aria-hidden="true" style={{ color: CYAN_STRONG }} />
            <span>{t('reviewDemo.dataGovernance.title')}</span>
          </h2>
          <div
            data-testid="review-demo-data-governance-grid"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: 16,
              marginTop: 12,
            }}
          >
            <div data-testid="review-demo-data-governance-operational" style={{ padding: 14, borderRadius: 10, background: 'rgba(2,6,23,0.55)', border: `1px solid ${RED_SOFT}` }}>
              <p style={{ fontSize: 11, letterSpacing: 1.2, fontWeight: 700, color: '#F87171', margin: 0, textTransform: 'uppercase' }}>
                {t('reviewDemo.dataGovernance.operationalLabel')}
              </p>
              <p style={{ ...bodyStyle, marginTop: 8, marginBottom: 0, fontSize: 14 }}>
                {t('reviewDemo.dataGovernance.operationalBody')}
              </p>
            </div>
            <div data-testid="review-demo-data-governance-process" style={{ padding: 14, borderRadius: 10, background: 'rgba(2,6,23,0.55)', border: '1px solid rgba(56,189,248,0.30)' }}>
              <p style={{ fontSize: 11, letterSpacing: 1.2, fontWeight: 700, color: CYAN_STRONG, margin: 0, textTransform: 'uppercase' }}>
                {t('reviewDemo.dataGovernance.processLabel')}
              </p>
              <p style={{ ...bodyStyle, marginTop: 8, marginBottom: 0, fontSize: 14 }}>
                {t('reviewDemo.dataGovernance.processBody')}
              </p>
            </div>
          </div>
          <p data-testid="review-demo-data-governance-principle" style={{ ...bodyStyle, marginTop: 16, marginBottom: 0, fontWeight: 600, color: 'rgba(226,232,240,0.92)' }}>
            {t('reviewDemo.dataGovernance.principle')}
          </p>
        </section>

        {/* CTA row */}
        <div
          data-testid="review-demo-cta-row"
          style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 34, marginBottom: 22 }}
        >
          <Link
            to="/moje-akta-imigracyjne"
            data-testid="review-demo-cta-moje-akta"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '12px 22px', borderRadius: 8, fontWeight: 700, background: CYAN, color: '#0B1220', textDecoration: 'none', fontSize: 15 }}
          >
            <Users className="w-4 h-4" aria-hidden="true" />
            {t('reviewDemo.ctas.primaryLabel')}
          </Link>
          <Link
            to="/dla-instytucji"
            data-testid="review-demo-cta-dla-instytucji"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '12px 22px', borderRadius: 8, fontWeight: 600, background: 'rgba(15,23,42,0.55)', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(226,232,240,0.92)', textDecoration: 'none', fontSize: 14 }}
          >
            <Building2 className="w-4 h-4" aria-hidden="true" />
            {t('reviewDemo.ctas.secondaryLabel')}
          </Link>
          <Link
            to="/dla-profesjonalistow"
            data-testid="review-demo-cta-dla-profesjonalistow"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '12px 22px', borderRadius: 8, fontWeight: 600, background: 'transparent', border: '1px solid rgba(148,163,184,0.30)', color: 'rgba(226,232,240,0.85)', textDecoration: 'none', fontSize: 14 }}
          >
            <Handshake className="w-4 h-4" aria-hidden="true" />
            {t('reviewDemo.ctas.tertiaryLabel')}
          </Link>
        </div>

        <p data-testid="review-demo-doctrine-footer" style={{ marginTop: 20, fontSize: 12, lineHeight: 1.6, color: 'rgba(226,232,240,0.55)', fontStyle: 'italic' }}>
          AI przygotowuje. Człowiek decyduje. Każde działanie zostawia ślad audytowy.
        </p>
      </div>
    </main>
  );
};

export default ReviewAktImigracyjnych;
